/**
 * The autopilot's one tick: forecast, follow the shots in flight, pick a target, mark the danger,
 * steer, and decide on the fire button and the Nova. It reads the game and never writes it; its own
 * memory is the stick it is holding, the target in hand, and its own dice (never the game's).
 *
 * Skill below 1 does not make it stupid. It commits to each push of the stick for a few ticks (and
 * plans knowing that it will), misjudges by a few ticks where things in flight will be, aims a few
 * pixels off, and waits a beat before trusting a shot.
 */

import * as math from '../sim/math';
import * as sim from '../sim';
import type { GameState, Input } from '../sim';
import { Danger } from './danger';
import { Forecast } from './forecast';
import { Gunnery } from './gunnery';
import * as horizon from './horizon';
import { Steer } from './steer';
import { Targeting } from './targeting';

// Local copies (see horizon.ts).
const { clamp } = math;
const { FIELD, FIGHTER, NOVA, Rng } = sim;
const { LETHAL, PACE } = horizon;

/** Every input there is, made once: `left | right << 1 | fire << 2 | bomb << 3`. */
const INPUTS: readonly Input[] = Array.from({ length: 16 }, (_, i) => ({
  left: (i & 1) !== 0,
  right: (i & 2) !== 0,
  fire: (i & 4) !== 0,
  bomb: (i & 8) !== 0,
}));
const IDLE: Input = INPUTS[0] ?? sim.NO_INPUT;

/** A Nova is for a ship that will be lost within this many ticks whatever it does (03 §3). */
const PANIC = 5;
/**
 * At no skill at all: ticks a push of the stick is kept, pixels of aim error, ticks its eye is out
 * when leading a target and when judging what is coming at it, ticks before it trusts a shot.
 */
const WORST_HOLD = 10;
const WORST_AIM = 10;
const WORST_LEAD = 30;
const WORST_SLIP = 10;
const WORST_TRIGGER = 5;
/** The errors are drawn afresh this often, and with every shot. */
const BLINK_EVERY = 40;

export class Pilot {
  private readonly rng: sim.Rng;
  private readonly forecast = new Forecast();
  private readonly gun = new Gunnery();
  private readonly danger = new Danger();
  private readonly steer = new Steer();
  private readonly targeting = new Targeting();
  private readonly hold: number;
  private readonly aimError: number;
  private readonly leadError: number;
  private readonly eyeError: number;
  private readonly trigger: number;
  private lastTick = -1;
  /** The stick (-1, 0, 1), and how many more ticks it is committed for. */
  private move = 0;
  private held = 0;
  /** This moment's errors: pixels off in aim, ticks out in leading a target, ticks out in judging a threat. */
  private wobble = 0;
  private lead = 0;
  private slip = 0;
  /** Ticks in a row that a volley has looked good. */
  private sure = 0;

  constructor(seed: number, skill: number) {
    const lack = 1 - clamp(skill, 0, 1);
    this.rng = new Rng(seed);
    this.hold = Math.round(lack * WORST_HOLD);
    this.aimError = Math.round(lack * WORST_AIM);
    this.leadError = Math.round(lack * WORST_LEAD);
    this.eyeError = Math.round(lack * WORST_SLIP);
    this.trigger = Math.round(lack * WORST_TRIGGER);
  }

  next(s: GameState): Input {
    const p = s.player;
    // The clock has gone back (a new game), or there is no ship to fly: let go of everything.
    if (s.tick < this.lastTick) this.letGo();
    this.lastTick = s.tick;
    if (s.phase === 'gameover' || (p.status !== 'flying' && p.status !== 'arriving')) {
      this.letGo();
      return IDLE;
    }
    const edge = FIGHTER.margin + (p.dual ? FIGHTER.dualOffset : 0);
    const lo = edge;
    const hi = FIELD.w - edge;
    if (s.tick % BLINK_EVERY === 0) this.blink();

    this.forecast.build(s);
    this.gun.survey(s, this.forecast);
    this.targeting.choose(s, this.forecast, this.gun, p.x, lo, hi, this.lead, this.rng);

    // The stick is spoken for until `held` runs out: plan from where that leaves the ship.
    const t0 = this.held;
    const start = clamp(p.x + this.move * FIGHTER.speed * t0, lo, hi);
    this.danger.build(s, this.forecast, start, this.slip);
    const planned = this.steer.plan(this.danger, this.targeting.goal + this.wobble, t0, lo, hi, this.hold + 1);
    if (this.held > 0) this.held--;
    else if (planned !== this.move) {
      this.move = planned;
      this.held = this.hold;
    }
    const x = clamp(p.x + this.move * FIGHTER.speed, lo, hi);
    const fire = this.shoot(s, x);
    const bomb = this.hemmedIn(s, t0, lo, hi);
    return INPUTS[(this.move < 0 ? 1 : 0) | (this.move > 0 ? 2 : 0) | (fire ? 4 : 0) | (bomb ? 8 : 0)] ?? IDLE;
  }

  private letGo(): void {
    this.move = 0;
    this.held = 0;
    this.sure = 0;
  }

  /** Press (or keep holding) fire this tick, the ship having moved to `x`? */
  private shoot(s: GameState, x: number): boolean {
    const gun = this.gun;
    const rapid = s.powers.rapid > 0;
    const most = rapid ? NOVA.rapidVolleys : FIGHTER.maxVolleys;
    // The engine fires on the press: the button has to come up between shots, except with Rapid.
    if (gun.volleys >= most || (s.player.fireHeld && !rapid)) return false;
    const t = this.targeting;
    gun.volley(s, this.forecast, x, this.wobble, 1, t.mark, this.lead);
    if (gun.vetoed || gun.hits === 0) {
      this.sure = 0;
      return false;
    }
    // The last volley is kept for a prize that is nearly lined up, unless this one is out of the way first.
    if (t.prize && !gun.onMark && gun.volleys === most - 1 && gun.last >= t.fireAt) return false;
    if (++this.sure <= this.trigger) return false;
    this.blink();
    return true;
  }

  /** A fresh set of errors; none at full skill. */
  private blink(): void {
    if (this.aimError > 0) this.wobble = this.rng.int(2 * this.aimError + 1) - this.aimError;
    if (this.leadError > 0) this.lead = this.rng.int(2 * this.leadError + 1) - this.leadError;
    if (this.eyeError > 0) this.slip = this.rng.int(2 * this.eyeError + 1) - this.eyeError;
  }

  /** Spend a Nova: only when a ship is about to be lost and no move saves it (03 §3). */
  private hemmedIn(s: GameState, t0: number, lo: number, hi: number): boolean {
    const p = s.player;
    if (s.mode !== 'supernova' || s.powers.novas <= 0 || s.powers.shield || p.bombHeld) return false;
    // A Nova strikes everything in the air, a captive with the rest: not while one is flying.
    if (this.forecast.captiveAloft) return false;
    const d = this.danger;
    // What the committed stick runs into before the plan even starts.
    for (let k = 1; k <= Math.min(t0, PANIC); k++) {
      const x = clamp(p.x + this.move * FIGHTER.speed * k, lo, hi);
      if (d.at(k, Math.round((x - d.x) / PACE)) === LETHAL) return true;
    }
    return this.steer.crashAt > 0 && this.steer.crashAt <= PANIC;
  }
}
